import { useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { OpenAPIV3_1 as OpenApi } from 'openapi-types';
import { WorkspaceKeyPermission } from '~/types/WorkspaceKeyTypes';
import { externalApi } from '~/utils/api/external';
import { ApiInstanceConfig, apiInstance } from '~/utils/api/client';
import { UseApiQueryOptions, formatErrorPayload, validateApiResponse } from './utils';

export type ExternalApiEndpoint = Omit<OpenApi.OperationObject, 'operationId'> & {
  method: OpenApi.HttpMethods;
  path: string;
  operationId: string;
  permissions: WorkspaceKeyPermission[];
};

export type ExternalApi = OpenApi.Document & { endpoints: Record<string, ExternalApiEndpoint> };

const getOpenApiSpec = () => externalApi.get<OpenApi.Document>(import.meta.env.VITE_OPENAPI_URL);

const ROOT_KEY = ['external_api'];

export const useExternalApiQuery = (
  options?: UseApiQueryOptions<typeof getOpenApiSpec, ExternalApi>,
) => {
  const dispatch = useDispatch();

  return useQuery(
    ROOT_KEY,
    async () => {
      const response = validateApiResponse(await getOpenApiSpec()).data;

      const endpoints = Object.entries(response.paths ?? {}).reduce<
        Record<string, ExternalApiEndpoint>
      >((acc, [path, methods]) => {
        Object.entries(methods ?? {}).forEach(([method, body]) => {
          if (typeof body !== 'string' && 'operationId' in body && body.operationId !== undefined) {
            acc[body.operationId] = {
              ...body,
              operationId: body.operationId,
              method: method as OpenApi.HttpMethods,
              permissions: (Object.values(body.security?.[0] ?? {})[0] ?? [])
                .map((item) => item.split('.')[1])
                .filter(Boolean) as WorkspaceKeyPermission[],
              path,
            };
          }
        });

        return acc;
      }, {});

      return { ...response, endpoints };
    },
    {
      onError: (error: Error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

const externalApiRequest = ({
  url,
  method = 'get',
  apiKey,
  ...rest
}: ApiInstanceConfig & { apiKey: string }) =>
  apiInstance({
    url,
    headers: {
      Authorization: `RC-WSKEY ${apiKey}`,
    },
  }).any({ method, ...rest });

export const useRequestMutation = () => {
  const dispatch = useDispatch();

  return useMutation(
    async (params: ApiInstanceConfig & { apiKey: string }) => {
      const response = await externalApiRequest(params);
      return response.data;
    },
    {
      onError: (error: Error) => dispatch(formatErrorPayload(error)),
    },
  );
};
